import { useEffect, useState } from 'react';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  updateDoc
} from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import type { User } from 'firebase/auth';
import type { JobApplication } from '@shared/types';
import { STATUS_LABELS, STATUS_COLORS } from '@shared/constants';
import Navigation from '../components/Navigation';

interface ArchivedApplicationsPageProps {
  user: User;
}

export default function ArchivedApplicationsPage({ user }: ArchivedApplicationsPageProps) {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');

  useEffect(() => {
    loadArchivedApplications();
  }, [user.uid]);

  const loadArchivedApplications = async () => {
    setLoading(true);
    try {
      const db = getFirestore();
      const appsQuery = query(
        collection(db, 'applications'),
        where('userId', '==', user.uid),
        where('archived', '==', true),
        orderBy('appliedDate', 'desc')
      );

      const snapshot = await getDocs(appsQuery);
      const apps: JobApplication[] = [];
      snapshot.forEach((doc) => {
        apps.push({ id: doc.id, ...doc.data() } as JobApplication);
      });
      setApplications(apps);
    } catch (error) {
      console.error('Error loading archived applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchive = async (appId: string) => {
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'applications', appId), {
        archived: false
      });
      setApplications(apps => apps.filter(app => app.id !== appId));
    } catch (error) {
      console.error('Error unarchiving application:', error);
    }
  };

  const handleDownload = async (path?: string) => {
    if (!path) return;
    try {
      const storage = getStorage();
      const url = await getDownloadURL(ref(storage, path));
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const filteredApplications = applications.filter(app => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();
    return (
      app.company?.toLowerCase().includes(text) ||
      app.jobTitle?.toLowerCase().includes(text) ||
      app.location?.toLowerCase().includes(text) ||
      app.notes?.toLowerCase().includes(text)
    );
  });

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation user={user} />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="space-y-4 sm:space-y-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">Archived Applications</h2>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
              View and restore your archived job applications
            </p>
          </div>

          {/* Search */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <input
              type="text"
              placeholder="Search archived applications..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Results Count */}
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {filteredApplications.length} archived application{filteredApplications.length !== 1 ? 's' : ''} found
          </div>

          {/* Applications List */}
          {loading ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">Loading...</div>
          ) : filteredApplications.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 sm:p-12 text-center">
              <div className="text-gray-400 text-4xl sm:text-5xl mb-4">📦</div>
              <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">No archived applications</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {searchText
                  ? 'No applications match your search'
                  : 'Applications you archive will appear here'}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="sm:hidden space-y-3">
                {filteredApplications.map((app) => (
                  <div key={app.id} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{app.company}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{app.jobTitle}</div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[app.status]}`}>
                        {STATUS_LABELS[app.status]}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                      {app.location && <span>{app.location} • </span>}
                      {formatDate(app.appliedDate)}
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex gap-3">
                        {app.jobUrl && (
                          <a
                            href={app.jobUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                          >
                            Job Post
                          </a>
                        )}
                        {app.resumePath && (
                          <button
                            onClick={() => handleDownload(app.resumePath)}
                            className="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                          >
                            Resume
                          </button>
                        )}
                        {app.coverLetterPath && (
                          <button
                            onClick={() => handleDownload(app.coverLetterPath)}
                            className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                          >
                            Cover Letter
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => app.id && handleUnarchive(app.id)}
                        className="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Company / Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Location
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Applied Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Links
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredApplications.map((app) => (
                        <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">{app.company}</div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">{app.jobTitle}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {app.location || '-'}
                          </td>
                          <td className="px-6 py-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${STATUS_COLORS[app.status]}`}>
                              {STATUS_LABELS[app.status]}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(app.appliedDate)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex gap-2">
                              {app.jobUrl && (
                                <a
                                  href={app.jobUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                >
                                  Job Post
                                </a>
                              )}
                              {app.resumePath && (
                                <button
                                  onClick={() => handleDownload(app.resumePath)}
                                  className="text-xs text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300"
                                >
                                  Resume
                                </button>
                              )}
                              {app.coverLetterPath && (
                                <button
                                  onClick={() => handleDownload(app.coverLetterPath)}
                                  className="text-xs text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300"
                                >
                                  Cover Letter
                                </button>
                              )}
                              {!app.jobUrl && !app.resumePath && !app.coverLetterPath && (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button
                              onClick={() => app.id && handleUnarchive(app.id)}
                              className="text-sm text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 font-medium"
                            >
                              Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
